import { useEffect } from "react";
import { isPreviewMode } from "@/lib/content";

// Mounted only inside the admin's live-preview iframe. It makes the rendered
// page act like an editable canvas: hovering highlights elements, and clicking
// one tells the parent editor what you picked (so it can focus the matching
// field) instead of navigating away.
export default function PreviewBridge() {
  useEffect(() => {
    if (!isPreviewMode()) return;

    const style = document.createElement("style");
    style.textContent = `
      .cli-pick-hover { outline: 2px solid #a855f7 !important; outline-offset: 2px; cursor: pointer !important; border-radius: 2px; }
      .cli-editing { outline: 2px dashed #7c3aed !important; outline-offset: 2px; background: rgba(124,58,237,0.05); cursor: text !important; }
      html.cli-preview * { cursor: default; }
    `;
    document.head.appendChild(style);
    document.documentElement.classList.add("cli-preview");

    let hovered = null;
    const clear = () => { if (hovered) { hovered.classList.remove("cli-pick-hover"); hovered = null; } };
    const isPickable = (el) =>
      el && el.nodeType === 1 && el !== document.body &&
      (el.tagName === "IMG" || (el.textContent || "").trim().length > 0);

    const onOver = (e) => {
      const el = e.target;
      if (!isPickable(el)) return;
      clear();
      hovered = el;
      el.classList.add("cli-pick-hover");
    };
    const onOut = () => clear();

    let editingEl = null;
    let editingOld = "";

    const onClick = (e) => {
      if (editingEl) return; // let clicks work normally while inline-editing
      const el = e.target;
      // Always stop the page from navigating/submitting while editing.
      e.preventDefault();
      e.stopPropagation();
      let kind = "text";
      let value = "";
      if (el.tagName === "IMG") { kind = "image"; value = el.getAttribute("src") || ""; }
      else { value = (el.innerText || el.textContent || "").trim().slice(0, 300); }
      if (!value) return;
      try { window.parent?.postMessage({ type: "cli-pick", kind, value }, window.location.origin); } catch { /* ignore */ }
    };

    // Double-click a text element to edit it right on the page.
    const finishEdit = (commit) => {
      if (!editingEl) return;
      const el = editingEl;
      editingEl = null;
      el.removeAttribute("contenteditable");
      el.classList.remove("cli-editing");
      const newText = (el.innerText || "").trim();
      if (commit && newText && newText !== editingOld) {
        try { window.parent?.postMessage({ type: "cli-edit", oldText: editingOld, newText }, window.location.origin); } catch { /* ignore */ }
      } else if (!commit) {
        el.innerText = editingOld; // revert on Escape
      }
    };

    const onDblClick = (e) => {
      const el = e.target;
      if (!el || el.nodeType !== 1 || el.tagName === "IMG") return;
      const text = (el.innerText || "").trim();
      if (!text) return;
      e.preventDefault();
      e.stopPropagation();
      editingEl = el;
      editingOld = text;
      clear();
      el.classList.add("cli-editing");
      el.setAttribute("contenteditable", "true");
      el.focus();
      try {
        const r = document.createRange();
        r.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      } catch { /* ignore */ }
    };

    const onKeyDown = (e) => {
      if (!editingEl) return;
      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); finishEdit(true); }
      else if (e.key === "Escape") { e.preventDefault(); finishEdit(false); }
    };
    const onBlur = () => finishEdit(true);

    document.addEventListener("mouseover", onOver, true);
    document.addEventListener("mouseout", onOut, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("dblclick", onDblClick, true);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("blur", onBlur, true);
    return () => {
      document.removeEventListener("mouseover", onOver, true);
      document.removeEventListener("mouseout", onOut, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("dblclick", onDblClick, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("blur", onBlur, true);
      document.documentElement.classList.remove("cli-preview");
      style.remove();
    };
  }, []);

  return null;
}
